import { DateTime, Interval } from 'luxon';

import { Period, DateRange, PreDefinedPeriod, ALL_PERIODS, DateQuery } from './types';

const isValidPeriod = (value: string): value is Period => ALL_PERIODS.includes(value as Period);

export const isValidRangeQuery = (query: {
  period: string | null | undefined;
  from: DateQuery;
  to: DateQuery;
}): query is { period: Period; from: Date; to: Date } => {
  const { period, from, to } = query;
  if (!period || !isValidPeriod(period)) {
    return false;
  }
  if (period === 'custom') {
    if (!from || !to) {
      return false;
    }
    const fromDateTime = DateTime.fromJSDate(from).startOf('day');
    const toDateTime = DateTime.fromJSDate(to).startOf('day');
    const threeMonthsAgo = DateTime.now().startOf('day').minus({ months: 3 }).plus({ days: 1 });
    const startOfToday = DateTime.now().startOf('day');
    if (fromDateTime > toDateTime || fromDateTime < threeMonthsAgo || toDateTime > startOfToday) {
      return false;
    }
  }
  if (period !== 'custom' && (from || to)) {
    return false;
  }
  return true;
};

export const getIntervalForThePeriod = (period: Period, timezone: string, dateRange?: DateRange) => {
  const now = DateTime.now().setZone(timezone);
  switch (period) {
    case 'yesterday': {
      const start = now.minus({ days: 1 }).startOf('day');
      const end = start.plus({ days: 1 });
      return Interval.fromDateTimes(start, end);
    }
    case 'this-week': {
      const start = now.startOf('week');
      const end = start.plus({ weeks: 1 });
      return Interval.fromDateTimes(start, end);
    }
    case 'last-week': {
      const start = now.minus({ weeks: 1 }).startOf('week');
      const end = start.plus({ weeks: 1 });
      return Interval.fromDateTimes(start, end);
    }
    case 'this-month': {
      const start = now.startOf('month');
      const end = start.plus({ months: 1 });
      return Interval.fromDateTimes(start, end);
    }
    case 'last-month': {
      const start = now.minus({ months: 1 }).startOf('month');
      const end = start.plus({ months: 1 });
      return Interval.fromDateTimes(start, end);
    }
    case 'custom': {
      if (!dateRange) {
        throw new Error('dateRange not defined for custom period');
      }
      const { from, to } = dateRange;
      const start = DateTime.fromObject(
        { year: from.getFullYear(), month: from.getMonth() + 1, day: from.getDate() },
        { zone: timezone },
      );
      const end = DateTime.fromObject(
        { year: to.getFullYear(), month: to.getMonth() + 1, day: to.getDate() },
        { zone: timezone },
      ).plus({ days: 1 });
      return Interval.fromDateTimes(start, end);
    }
  }
};

export const getDisplayRangeFromPreDefinedPeriod = (period: PreDefinedPeriod) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { start, end } = getIntervalForThePeriod(period, timezone);
  const from = new Date(start.toISODate());
  const to = new Date(end.minus({ days: 1 }).toISODate());
  return { from, to };
};
